import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, Scroll, Shield, Loader2 } from "lucide-react";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Checkbox } from "@jambushrushti/components/ui/checkbox";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "signup">("login");
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    login,
    signup,
    isAuthenticated,
    isLoading: authLoading,
    user,
  } = useAdminAuth();

  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Only redirect after login completes with a valid redirect path
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, navigate]);

  if (authLoading) {
    return (
      <div
        className="lotus-pattern flex min-h-screen items-center justify-center p-4"
        style={{ backgroundColor: "#FAF7F2" }}
      >
        <Loader2
          className="h-8 w-8 animate-spin"
          style={{ color: "#D2811D" }}
        />
      </div>
    );
  }

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Password managers/browser autofill do not always emit React onChange.
    // Read the rendered inputs so the visible credentials are submitted.
    const submittedEmail = emailInputRef.current?.value.trim() || email.trim();
    const submittedPassword = passwordInputRef.current?.value || password;

    if (!submittedEmail || !submittedPassword) {
      toast({
        title: "Missing credentials",
        description: "Please enter your email and password",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(submittedEmail, submittedPassword, rememberMe);

      if (result.success && result.redirectTo) {
        toast({
          title: "Welcome back!",
          description: "You have successfully logged in",
        });
        setRedirectTo(result.redirectTo);
      } else {
        toast({
          title: "Login failed",
          description: result.error || "Invalid credentials. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await signup(email, password, "");

      if (result.success) {
        toast({
          title: "Account created!",
          description: "You can now log in with your credentials",
        });
        setMode("login");
        setPassword("");
      } else {
        toast({
          title: "Signup failed",
          description:
            result.error || "Could not create account. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="lotus-pattern flex min-h-screen items-center justify-center p-4"
      style={{ backgroundColor: "#FAF7F2" }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(210, 129, 29, 0.08)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(45, 42, 38, 0.05)" }}
        />
      </div>

      <Card className="shadow-elevated relative z-10 w-full max-w-md overflow-hidden rounded-2xl border-0 will-change-auto">
        <div className="absolute left-0 right-0 top-0 h-1 bg-gradient-to-r from-primary via-gold to-primary" />

        <CardHeader className="space-y-4 pt-8 text-center">
          <div className="flex justify-center">
            <div className="shadow-glow flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-gold">
              <Shield className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <div>
            <CardTitle className="font-display text-2xl font-bold text-foreground">
              Jambushrusti Portal
            </CardTitle>
            <CardDescription className="mt-2 text-muted-foreground">
              Admin & Content Management System
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="px-6 pb-8">
          <Tabs
            value={mode}
            onValueChange={v => setMode(v as "login" | "signup")}
            className="w-full"
          >
            <TabsList className="mb-6 grid w-full grid-cols-1">
              <TabsTrigger value="login" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Login
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-0">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={emailInputRef}
                      id="login-email"
                      type="email"
                      placeholder="admin@jambushrusti.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="h-11 rounded-xl border-border/50 pl-10"
                      disabled={isLoading}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="login-password"
                    className="text-sm font-medium"
                  >
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={passwordInputRef}
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="h-11 rounded-xl border-border/50 pl-10 pr-10"
                      disabled={isLoading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={() => void handleLogin()}
                  className="shadow-soft h-11 w-full rounded-xl bg-gradient-to-r from-primary to-gold font-medium text-primary-foreground transition-all hover:opacity-90"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-0">
              <form onSubmit={handleSignup} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="h-11 rounded-xl border-border/50 pl-10"
                      disabled={isLoading}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="signup-password"
                    className="text-sm font-medium"
                  >
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="h-11 rounded-xl border-border/50 pl-10 pr-10"
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="shadow-soft h-11 w-full rounded-xl bg-gradient-to-r from-primary to-gold font-medium text-primary-foreground transition-all hover:opacity-90"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
            >
              <Scroll className="h-4 w-4" />
              Return to Main Website
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
